import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL } from '../utils/constants';
import { AppThunk } from './store';

import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { GAFScaleItem } from '../Models/GAFScale/GAFScaleItem.model';
import { GAFScaleModel } from '../Models/GAFScale/GAFScale.model';
import { preparePriorAssessmentForNewDraft } from '../utils/aiFlagHelpers';

const gafScaleSlice = createSlice({
    name: 'gafScaleSlice',
    initialState: {
        gafScaleInfo: new GAFScaleModel(),
        gafScaleOptions: [] as GAFScaleItem[],
        isFetchingGAFScale: false,
    },
    reducers: {
        _setGAFScaleInfo: (state, action) => {
            state.gafScaleInfo = action.payload;
        },
        _setGAFScaleOptions: (state, action) => {
            state.gafScaleOptions = action.payload;
        },
        _setIsFetchingGAFScale: (state, action) => {
            state.isFetchingGAFScale = action.payload;
        },
    },
});

const { _setGAFScaleInfo, _setGAFScaleOptions, _setIsFetchingGAFScale } = gafScaleSlice.actions;

export const setGAFScaleInfo =
    (gafScaleInfo: GAFScaleModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setGAFScaleInfo(gafScaleInfo));
    };

export const setGAFScaleOptions =
    (options: GAFScaleItem[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setGAFScaleOptions(options));
    };

export const setIsFetchingGAFScale =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingGAFScale(isFetching));
    };

export const saveGAFScaleAssessment =
    (gafScale: GAFScaleModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        const isUpdate = Boolean(gafScale.Id);
        const url = isUpdate ? `${API_URL}/gafscale/updategafscale/` : `${API_URL}/gafscale/insertgafscale/`;

        try {
            const response = await axios.put(url, gafScale);
            const savedGAFScale = isUpdate ? gafScale : response.data;
            if (Boolean(response.data)) {
                dispatch(setGAFScaleInfo(isUpdate ? gafScale : { ...gafScale, Id: response.data.Id }));
            }
            const isDraftSaved = await dispatch(prepareGAFScaleNotes(savedGAFScale));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareGAFScaleNotes =
    (gafScale: GAFScaleModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(gafScale.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.GAFScale);
            summaryNotes.push({
                Id: gafScale.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Global Assessment of Functioning Scale Assessment Completed',
                Type: SummaryNoteTypeEnum.GAFScale,
                IsConfirm: true,
                DisplayName: 'GAF Scale',
            });
            engagementDraft.SummaryNotes = summaryNotes;
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                GAFScaleStatus: gafScale.AssessmentStatus,
            };
            const updatedEngagementDraft = {
                ...engagementDraft,
                GAFScaleId: gafScale.Id,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
            };
            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchGAFScaleInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingGAFScale(true));
        const response = await axios.get(`${API_URL}/gafscale/fetchgafscalebyid?id=${id}`);
        dispatch(setGAFScaleInfo(response.data));
        dispatch(setIsFetchingGAFScale(false));
    };

export const fetchGAFScaleInfoByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingGAFScale(true));
        const response = await axios.get(`${API_URL}/gafscale/fetchgafscalebymemberid?memberId=${memberId}`);
        if (response.data?.Id) {
            preparePriorAssessmentForNewDraft(response.data);
            dispatch(setGAFScaleInfo(response.data));
        } else {
            dispatch(setGAFScaleInfo(new GAFScaleModel()));
        }
        dispatch(setIsFetchingGAFScale(false));
    };

export const fetchGAFScaleOptions = (): AppThunk => async (dispatch, getState) => {
    dispatch(setIsFetchingGAFScale(true));
    const response = await axios.get(`${API_URL}/gafscale/fetchgafscaleoptions`);
    dispatch(setGAFScaleOptions(response.data));
    dispatch(setIsFetchingGAFScale(false));
};

export default gafScaleSlice.reducer;
